import {
  ViewStyle,
  TouchableOpacity,
  TouchableOpacityProps,
  ActivityIndicator,
  View,
} from "react-native"
import { Text } from "./Typography"
import { functionalColors } from "./colors"
import { ReactNode } from "react"

type ButtonTypes =
  | "primary"
  | "primary2"
  | "secondary"
  | "tertiary"
  | "tertiary2"
  | "bilbao"
  | "white2"
  | "white3"

type ButtonProps = TouchableOpacityProps & {
  type?: ButtonTypes
  title: string
  loading?: boolean
  loadingColor?: string
  lowercase?: boolean
  textColor?: keyof (typeof functionalColors)["text"]
  rightIcon?: ReactNode
  // Plaid requires the button to be disabled
  // but we want to preserve the enabled button view
  disabledPress?: boolean
}

type Styles = {
  [buttonType in ButtonTypes]: {
    outer: ViewStyle
    inner: ViewStyle
    disabled: ViewStyle
  }
}

export function Button3D(props: ButtonProps) {
  const {
    type = "primary",
    title,
    loading,
    lowercase,
    textColor,
    rightIcon = null,
    loadingColor = "#ffffff",
  } = props

  const primaryButtonStyles = {
    outer: {
      width: "100%",
      height: 60,
      backgroundColor: functionalColors.button.primary3D.outerBackgroundColor,
      borderRadius: 100,
    },
    inner: {
      width: "100%",
      height: props.disabled ? 60 : 54,
      backgroundColor: functionalColors.button.primary3D.innerBackgroundColor,
      borderRadius: 100,
      alignItems: "center",
      justifyContent: "center",
    },
    disabled: {
      backgroundColor: functionalColors.button.primary3D.disabledBackgroundColor,
    },
  } as Styles["primary"]

  const tertiaryButtonStyles = {
    outer: {
      width: "100%",
      height: 60,
      backgroundColor: functionalColors.button.tertiary3D.outerBackgroundColor,
      borderRadius: 100,
      borderWidth: 2,
      borderColor: functionalColors.button.tertiary3D.borderColor,
    },
    inner: {
      width: "100%",
      height: props.disabled ? 60 : 54,
      backgroundColor: functionalColors.button.tertiary3D.innerBackgroundColor,
      borderRadius: 100,
      alignItems: "center",
      justifyContent: "center",
    },
    disabled: {},
  } as Styles["tertiary"]

  const styles: Styles = {
    primary: primaryButtonStyles,
    primary2: {
      ...primaryButtonStyles,
      outer: {
        ...primaryButtonStyles.outer,
        borderRadius: 14,
      },
      inner: {
        ...primaryButtonStyles.inner,
        borderRadius: 14,
      },
    },
    secondary: {
      outer: {
        width: "100%",
        height: 60,
        backgroundColor: functionalColors.button.secondary3D.outerBackgroundColor,
        borderRadius: 100,
        borderWidth: 2,
        borderColor: functionalColors.button.secondary3D.borderColor,
      },
      inner: {
        width: "100%",
        height: props.disabled ? 60 : 54,
        backgroundColor: functionalColors.button.secondary3D.innerBackgroundColor,
        borderRadius: 100,
        alignItems: "center",
        justifyContent: "center",
      },
      disabled: {},
    },
    tertiary: {
      outer: {
        width: "100%",
        height: 60,
        backgroundColor: functionalColors.button.tertiary3D.outerBackgroundColor,
        borderRadius: 100,
        borderWidth: 2,
        borderColor: functionalColors.button.tertiary3D.borderColor,
      },
      inner: {
        width: "100%",
        height: props.disabled ? 60 : 54,
        backgroundColor: functionalColors.button.tertiary3D.innerBackgroundColor,
        borderRadius: 100,
        alignItems: "center",
        justifyContent: "center",
      },
      disabled: {},
    },
    tertiary2: {
      ...tertiaryButtonStyles,
      outer: {
        ...tertiaryButtonStyles.outer,
        borderRadius: 14,
      },
      inner: {
        ...tertiaryButtonStyles.inner,
        borderRadius: 14,
      },
    },
    bilbao: {
      ...primaryButtonStyles,
      outer: {
        ...primaryButtonStyles.outer,
        backgroundColor: functionalColors.button.bilbao3D.outerBackgroundColor,
        borderRadius: 14,
      },
      inner: {
        ...primaryButtonStyles.inner,
        backgroundColor: functionalColors.button.bilbao3D.innerBackgroundColor,
        borderRadius: 14,
      },
    },
    white2: {
      ...primaryButtonStyles,
      outer: {
        ...primaryButtonStyles.outer,
        backgroundColor: functionalColors.button.white23D.outerBackgroundColor,
        borderRadius: 14,
      },
      inner: {
        ...primaryButtonStyles.inner,
        backgroundColor: functionalColors.button.white23D.innerBackgroundColor,
        borderRadius: 14,
      },
    },
    white3: {
      ...primaryButtonStyles,
      outer: {
        ...primaryButtonStyles.outer,
        backgroundColor: functionalColors.button.white33D.outerBackgroundColor,
        borderRadius: 18,
        borderWidth: 2,
        borderColor: functionalColors.button.white33D.outerBackgroundColor,
        height: 62,
      },
      inner: {
        ...primaryButtonStyles.inner,
        backgroundColor: functionalColors.button.white33D.innerBackgroundColor,
        borderRadius: 18,
      },
    },
  }
  let outerAppliedStyles = {}
  if (type && styles[type]) {
    outerAppliedStyles = {
      ...outerAppliedStyles,
      ...styles[type].outer,
    }
  }
  let innerAppliedStyles = {}
  if (type && styles[type]) {
    innerAppliedStyles = {
      ...innerAppliedStyles,
      ...styles[type].inner,
    }
  }
  if (props.disabled) {
    outerAppliedStyles = {
      ...outerAppliedStyles,
      ...styles[type].disabled,
    }
    innerAppliedStyles = {
      ...innerAppliedStyles,
      ...styles[type].disabled,
    }
  }

  const baseInnerStyles = {
    flexDirection: "row",
  }

  return (
    <TouchableOpacity
      {...props}
      disabled={props.disabled || props.loading || props.disabledPress}
      style={outerAppliedStyles}
    >
      <View style={[baseInnerStyles, innerAppliedStyles]}>
        <Text
          type={type === "primary" || type === "primary2" ? "primaryButton" : "secondaryButton"}
          color={
            textColor
              ? textColor
              : type === "primary" || type === "primary2"
              ? "quaternary"
              : "primary"
          }
        >
          {loading ? (
            <ActivityIndicator size="small" color={loadingColor} />
          ) : lowercase ? (
            title
          ) : (
            title.toUpperCase()
          )}
        </Text>
        {!!rightIcon && <View style={{ marginLeft: 10 }}>{rightIcon}</View>}
      </View>
    </TouchableOpacity>
  )
}
